import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, CircularProgress, Paper } from "@material-ui/core";
import * as ROUTES from "../../constants/route";

export const isAttestationEligible = (stats, uploadContext) =>
  Number(stats?.tier) !== 3 && (uploadContext === "long-kyc"
    ? ["LONG_KYC-1", "LONG_KYC-4"]
    : ["LONG_KYC-1", "LONG_KYC-4", "LONG_KYC-6"]
  ).includes(stats?.status);

// Root account state, not AuthUserContext or legacy KYC flags, owns eligibility.
export const useAttestationEligibility = (firebase, uid, uploadContext) => {
  const [account, setAccount] = useState({
    generation: null, data: null, loaded: false, checking: false, error: "",
  });
  const generationRef = useRef(null);
  const identityRef = useRef(null);
  const eligibleRef = useRef(false);
  identityRef.current = { firebase, uid, uploadContext };

  const isCurrent = useCallback((generation) =>
    generation && generation.active && generationRef.current === generation &&
    generation.firebase === identityRef.current.firebase &&
    generation.uid === identityRef.current.uid &&
    generation.uploadContext === identityRef.current.uploadContext,
  []);

  const applySnapshot = useCallback((snapshot, generation) => {
    const data = snapshot.data() || {};
    generation.revision++;
    generation.data = data;
    eligibleRef.current = isAttestationEligible(data.stats, uploadContext);
    setAccount((previous) => ({ ...previous, generation, data, error: "", loaded: true }));
    return data;
  }, [uploadContext]);

  const failClosed = useCallback((generation) => {
    if (!isCurrent(generation)) return;
    generation.revision++;
    generation.data = null;
    eligibleRef.current = false;
    setAccount((previous) => ({
      ...previous, generation, loaded: true,
      error: "We couldn't confirm your verification status. Please retry before continuing.",
    }));
  }, [isCurrent]);

  const refresh = useCallback(async () => {
    const generation = generationRef.current;
    if (!isCurrent(generation) || generation.firebase !== firebase ||
        generation.uid !== uid || generation.uploadContext !== uploadContext) return null;
    // Concurrent hook/form callers await the same read rather than invalidating each other.
    if (generation.pending) return generation.pending;
    if (!firebase || !uid) {
      failClosed(generation);
      return null;
    }
    const revision = generation.revision;
    const pending = (async () => {
      setAccount((previous) => ({ ...previous, checking: true }));
      try {
        const snapshot = await firebase.db.collection("users").doc(uid).get({ source: "server" });
        if (!isCurrent(generation)) return null;
        // A later trusted listener update (or failure) supersedes this read, even if it finishes last.
        if (generation.revision !== revision) return generation.data;
        return applySnapshot(snapshot, generation);
      } catch {
        if (!isCurrent(generation)) return null;
        if (generation.revision !== revision) return generation.data;
        failClosed(generation);
        return null;
      }
    })();
    generation.pending = pending;
    try {
      return await pending;
    } finally {
      if (isCurrent(generation)) {
        generation.pending = null;
        setAccount((previous) => ({ ...previous, checking: false }));
      }
    }
  }, [firebase, uid, uploadContext, isCurrent, applySnapshot, failClosed]);

  useEffect(() => {
    const generation = {
      firebase, uid, uploadContext, active: true, revision: 0, data: null, pending: null,
    };
    generationRef.current = generation;
    setAccount({ generation, data: null, loaded: false, checking: false, error: "" });
    eligibleRef.current = false;
    refresh();
    const unsubscribe = firebase && uid
      ? firebase.db.collection("users").doc(uid).onSnapshot(
        { includeMetadataChanges: true },
        (snapshot) => {
          if (!isCurrent(generation)) return;
          if (!snapshot.metadata.fromCache && !snapshot.metadata.hasPendingWrites) {
            applySnapshot(snapshot, generation);
          }
        },
        () => failClosed(generation)
      )
      : null;
    return () => {
      generation.active = false;
      eligibleRef.current = false;
      if (unsubscribe) unsubscribe();
    };
  }, [firebase, uid, uploadContext, refresh, isCurrent, applySnapshot, failClosed]);

  const current = isCurrent(account.generation);
  const status = current ? account.data?.stats?.status || null : null;
  const eligible = current && account.loaded && !account.error && isAttestationEligible(account.data?.stats, uploadContext);
  // Gate the render and in-flight camera checks immediately, before effect cleanup/setup.
  eligibleRef.current = Boolean(eligible);

  return {
    status,
    data: current ? account.data : null,
    loaded: Boolean(current && account.loaded),
    checking: Boolean(current && account.checking),
    error: current ? account.error : "",
    eligible: Boolean(eligible),
    eligibleRef,
    refresh,
  };
};

export const AttestationStatusNotice = ({ account, history, onRetry, children }) => {
  const reviewing = ["LONG_KYC-2", "LONG_KYC-3", "LONG_KYC-5"].includes(account.status);
  const message = account.error || (reviewing
    ? "Your KYC application is under review. Video recording is unavailable while this review is pending."
    : Number(account.data?.stats?.tier) === 3 || account.status === "LONG_KYC-8"
      ? "Your account is already Tier 3. No video submission is needed."
      : !account.status
        ? "Your verification status is missing. Please contact online@rampver.com for help recovering your application."
        : "Video submission is not available at your current verification stage. Check your dashboard for the next step.");

  return (
    <Paper style={{ padding: 24, margin: "24px 0" }} elevation={2}>
      <h1 className="font-poppins" style={{ fontSize: 24, fontWeight: 700 }}>
        Video Attestation
      </h1>
      {!account.loaded ? (
        <div role="status"><CircularProgress size={24} /> Checking your verification status...</div>
      ) : (
        <>
          <p className="font-poppins" role="status">{message}</p>
          {children}
          <Button onClick={onRetry || account.refresh} disabled={account.checking}>
            Retry Status Check
          </Button>
          <Button onClick={() => history.push(ROUTES.DASHBOARD)} style={{ color: "#256141" }}>
            Back to Dashboard
          </Button>
          {!account.status && <a href="mailto:online@rampver.com">Contact Support</a>}
        </>
      )}
    </Paper>
  );
};
